// Preset detail landing + image carousel
import {
  calcDiscountPercent,
  escapeAttr,
  formatGroupedInt,
  formatPriceWithRub,
  presetSpecsPairs
} from '../../core/format.js';
import {
  openPhotoModalHero,
  isPointerInPresetDetailBottomBar,
  isPresetDetailGoCartActive,
  isPresetDetailPhotoOpenSuppressed
} from '../../app/photo-modal.js';
import { showView } from '../../app/navigation.js';
import {
  addToCartFromPreset,
  bindPresetDetailGoCartButton,
  hideCartPromoBarNow,
  syncPresetCartButtonsUi
} from '../cart/cart.js';
import { presetHasConfigurator } from '../../lib/template-config.js';
import { lazyImageHtml, refreshLazyImages } from '../../core/lazy-image.js';
import { openTemplateConfigurator } from './config-wizard.js';
import type { PresetFpsRow, PresetItem } from '../../types/api.js';

export type PresetCarouselApi = {
  goTo: (index: number, animate?: boolean) => void;
  getIndex: () => number;
};

export type PresetCarouselTapHandler = (index: number, ev: PointerEvent) => void;

export function attachPresetImageCarousel(
  carousel: HTMLElement | null,
  trackEl: HTMLElement | null,
  slideCount: number,
  dotsWrap: HTMLElement | null,
  onTap?: PresetCarouselTapHandler
): PresetCarouselApi | null {
  if (!carousel || !trackEl || slideCount <= 1) return null;
  const root = carousel;
  const track = trackEl;

  let idx = 0;
  let startX = 0;
  let curX = 0;
  let dragging = false;
  let dragDistance = 0;
  let lastMoveX = 0;
  let lastMoveT = 0;
  let lastVelocityX = 0;
  const dots = dotsWrap ? Array.from(dotsWrap.querySelectorAll('.preset-detail-dot')) : [];

  const SNAP_MS = 320;
  const SNAP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function width(): number {
    return root.clientWidth || 1;
  }

  function applyPx(px: number, animate: boolean): void {
    track.style.transition = animate ? 'transform ' + SNAP_MS + 'ms ' + SNAP_EASE : 'none';
    track.style.transform = 'translate3d(' + px + 'px, 0, 0)';
  }

  function setIndex(next: number, animate = true): void {
    idx = Math.max(0, Math.min(slideCount - 1, next));
    applyPx(-idx * width(), animate);
    dots.forEach((dot, i) => {
      dot.classList.toggle('preset-detail-dot-active', i === idx);
    });
  }

  function onDown(clientX: number, pointerId: number | null): void {
    dragging = true;
    dragDistance = 0;
    startX = clientX;
    curX = clientX;
    lastMoveX = clientX;
    lastMoveT = performance.now();
    lastVelocityX = 0;
    root.classList.add('preset-detail-carousel-dragging');
    if (pointerId != null) {
      try {
        root.setPointerCapture(pointerId);
      } catch {
        /* ignore */
      }
    }
    applyPx(-idx * width(), false);
  }

  function onMove(clientX: number): void {
    if (!dragging) return;
    const now = performance.now();
    const dt = now - lastMoveT;
    if (dt > 0) {
      lastVelocityX = (clientX - lastMoveX) / dt;
    }
    lastMoveX = clientX;
    lastMoveT = now;
    curX = clientX;
    const dx = curX - startX;
    dragDistance = Math.max(dragDistance, Math.abs(dx));
    const w = width();
    let px = -idx * w + dx;
    if (idx === 0 && dx > 0) px = dx * 0.4;
    else if (idx === slideCount - 1 && dx < 0) px = -idx * w + dx * 0.4;
    applyPx(px, false);
  }

  function onUp(pointerId: number | null, ev?: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    root.classList.remove('preset-detail-carousel-dragging');
    if (pointerId != null) {
      try {
        root.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
    }
    const dx = curX - startX;
    const w = width();
    const distanceThreshold = Math.min(40, Math.max(28, w * 0.12));
    const flingThreshold = 0.28;
    const recentFling = performance.now() - lastMoveT < 80;
    const velocity = recentFling ? lastVelocityX : 0;
    const swiped =
      dx > distanceThreshold ||
      velocity > flingThreshold ||
      dx < -distanceThreshold ||
      velocity < -flingThreshold;
    if (dx > distanceThreshold || velocity > flingThreshold) setIndex(idx - 1);
    else if (dx < -distanceThreshold || velocity < -flingThreshold) setIndex(idx + 1);
    else setIndex(idx);
    if (dragDistance <= 8 && !swiped && onTap && ev) {
      try {
        onTap(idx, ev);
      } catch {
        /* ignore */
      }
    }
  }

  root.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    onDown(e.clientX, e.pointerId);
  });
  root.addEventListener('pointermove', (e: PointerEvent) => {
    if (!dragging) return;
    if (e.pointerType === 'mouse' && (e.buttons & 1) === 0) {
      onUp(e.pointerId, e);
      return;
    }
    onMove(e.clientX);
    if (dragDistance > 2) e.preventDefault();
  });
  root.addEventListener('pointerup', (e: PointerEvent) => {
    onUp(e.pointerId, e);
  });
  root.addEventListener('pointercancel', (e: PointerEvent) => {
    onUp(e.pointerId, e);
  });
  root.addEventListener('lostpointercapture', () => {
    if (dragging) onUp(null);
  });
  root.addEventListener('dragstart', (e) => {
    e.preventDefault();
  });

  setIndex(0, false);
  window.addEventListener('resize', () => {
    setIndex(idx, false);
  });

  return {
    goTo: (next, animate = true) => setIndex(next, animate),
    getIndex: () => idx
  };
}

function renderFpsTable(rows: PresetFpsRow[]): string {
  const fhd = rows.filter((r) => String(r.resolution || '').toUpperCase() === 'FHD');
  const list = fhd.length ? fhd : rows;
  const games: string[] = [];
  const byGame: Record<string, PresetFpsRow[]> = {};
  for (const row of list) {
    const g = String(row.game || '').trim();
    if (!g) continue;
    if (!byGame[g]) {
      byGame[g] = [];
      games.push(g);
    }
    byGame[g].push(row);
  }
  if (!games.length) return '';
  let html = '<div class="preset-fps-wrap"><div class="preset-fps-head">';
  html += '<h3 class="preset-detail-section-title">🎮 ФПС в играх</h3>';
  html += '<span class="preset-fps-chip">FHD</span></div>';
  html += '<div class="preset-fps-table"><div class="preset-fps-row preset-fps-row-head">';
  html += '<span>Игра</span><span>Низкие</span><span>Средние</span><span>Высокие</span></div>';
  for (let i = 0; i < games.length; i++) {
    const game = games[i];
    const rowsForGame = byGame[game] || [];
    const low = rowsForGame.find((r) => r.settings === 'low')?.fps || '—';
    const med = rowsForGame.find((r) => r.settings === 'medium')?.fps || '—';
    const high = rowsForGame.find((r) => r.settings === 'high')?.fps || '—';
    html += '<div class="preset-fps-row"><span>' + escapeAttr(game) + '</span>';
    html += '<span class="preset-fps-val">' + escapeAttr(low) + '</span>';
    html += '<span class="preset-fps-val">' + escapeAttr(med) + '</span>';
    html += '<span class="preset-fps-val">' + escapeAttr(high) + '</span></div>';
  }
  html += '</div></div>';
  return html;
}

function renderAdvantages(items: string[]): string {
  if (!items.length) return '';
  let html = '<section class="preset-detail-section"><h3 class="preset-detail-section-title">🔥 Почему стоит выбрать нас?</h3><ul class="preset-advantages">';
  for (let i = 0; i < items.length; i++) {
    html += '<li>' + escapeAttr(items[i]) + '</li>';
  }
  html += '</ul></section>';
  return html;
}

export function showPresetDetail(item: PresetItem): void {
  try {
    const activePre = document.querySelector('.view-active');
    if (activePre?.id === 'view-presets') hideCartPromoBarNow();
  } catch {
    /* ignore */
  }
  const wrap = document.getElementById('preset-detail-image-wrap');
  const titleEl = document.getElementById('preset-detail-title');
  const priceEl = document.getElementById('preset-detail-price');
  const priceMetaEl = document.getElementById('preset-detail-price-meta');
  const descEl = document.getElementById('preset-detail-desc');
  const specsEl = document.getElementById('preset-detail-specs');
  const extrasEl = document.getElementById('preset-detail-extras');
  const bottomBar = document.getElementById('preset-detail-bottom-bar');
  if (!wrap || !titleEl || !priceEl || !descEl || !specsEl) return;

  const detailRoot = document.getElementById('view-preset-detail');
  if (detailRoot && item?.id != null) {
    detailRoot.setAttribute('data-active-preset-id', String(item.id));
  }

  const hasConfig = presetHasConfigurator(item);
  detailRoot?.classList.toggle('preset-detail--configurator', hasConfig);

  let imgs: string[] = [];
  if (Array.isArray(item.images) && item.images.length) imgs = item.images.filter(Boolean) as string[];
  if (!imgs.length && item.image) imgs = [item.image];

  if (imgs.length) {
    let track = '<div class="preset-detail-carousel" id="preset-detail-carousel">';
    track += '<div class="preset-detail-carousel-track" id="preset-detail-carousel-track">';
    for (let i = 0; i < imgs.length; i++) {
      track +=
        '<div class="preset-detail-carousel-slide">' +
        lazyImageHtml(imgs[i], { loading: i === 0 ? 'eager' : 'lazy' }) +
        '</div>';
    }
    track += '</div>';
    if (imgs.length > 1) {
      track += '<div class="preset-detail-dots" id="preset-detail-dots">';
      for (let d = 0; d < imgs.length; d++) {
        track +=
          '<span class="preset-detail-dot' +
          (d === 0 ? ' preset-detail-dot-active' : '') +
          '"></span>';
      }
      track += '</div>';
    }
    track += '</div>';
    wrap.innerHTML = track;
    wrap.classList.remove('hidden');
    refreshLazyImages(wrap);

    const carouselEl = document.getElementById('preset-detail-carousel');
    const trackEl = document.getElementById('preset-detail-carousel-track');
    const dotsWrapEl = document.getElementById('preset-detail-dots');
    const slideImgs = wrap.querySelectorAll('.preset-detail-carousel-slide img');

    let carouselApi: PresetCarouselApi | null = null;
    const openPresetPhoto = (galleryIndex: number, ev: PointerEvent): void => {
      if (isPresetDetailGoCartActive()) return;
      if (isPointerInPresetDetailBottomBar(ev)) return;
      if (isPresetDetailPhotoOpenSuppressed()) return;
      try {
        const heroImg = (slideImgs[galleryIndex] as HTMLImageElement | undefined) ||
          (slideImgs[0] as HTMLImageElement);
        openPhotoModalHero(heroImg, ev, {
          galleryUrls: imgs.length > 1 ? imgs : undefined,
          galleryIndex,
          onGalleryIndexChange: (i) => carouselApi?.goTo(i, false)
        });
      } catch {
        /* ignore */
      }
    };

    carouselApi = attachPresetImageCarousel(
      carouselEl,
      trackEl,
      imgs.length,
      dotsWrapEl,
      imgs.length > 1 ? openPresetPhoto : undefined
    );

    wrap.querySelectorAll('img').forEach((imgEl) => {
      const img = imgEl as HTMLImageElement;
      img.style.cursor = 'pointer';
      if (imgs.length > 1) return;
      img.addEventListener('click', (ev) => {
        if (isPresetDetailGoCartActive()) return;
        if (isPointerInPresetDetailBottomBar(ev)) return;
        if (isPresetDetailPhotoOpenSuppressed()) return;
        try {
          openPhotoModalHero(img, ev);
        } catch {
          /* ignore */
        }
      });
    });
  } else {
    wrap.innerHTML = '';
    wrap.classList.add('hidden');
  }

  titleEl.textContent = item.title || '';

  const discount =
    item.discountPercent != null
      ? Number(item.discountPercent)
      : calcDiscountPercent(item.price, item.oldPrice);
  const priceText = formatPriceWithRub(item.price);
  const oldPriceText = formatPriceWithRub(item.oldPrice);
  let priceHtml = '';
  if (priceText) {
    priceHtml += '<span class="preset-detail-price-main">' + escapeAttr(priceText) + '</span>';
  }
  if (oldPriceText) {
    priceHtml += '<span class="preset-detail-old-price">' + escapeAttr(oldPriceText) + '</span>';
  }
  if (priceText && discount != null && discount > 0) {
    priceHtml += '<span class="preset-detail-discount">-' + Math.round(discount) + '%</span>';
  }
  priceEl.innerHTML = priceHtml;
  priceEl.style.display = priceText ? '' : 'none';

  if (priceMetaEl) {
    if (item.installmentPrice != null && Number(item.installmentPrice) > 0) {
      priceMetaEl.textContent =
        'от ' + formatGroupedInt(item.installmentPrice) + ' ₽ /мес в рассрочку';
      priceMetaEl.hidden = false;
    } else {
      priceMetaEl.textContent = '';
      priceMetaEl.hidden = true;
    }
  }

  const pairs = presetSpecsPairs(item?.specs);
  let specsHtml = '<div class="preset-specs-card">';
  for (let sIdx = 0; sIdx < pairs.length; sIdx++) {
    const pair = pairs[sIdx];
    const label = pair[0];
    const value = pair[1] || '—';
    if (!value || value === '—') continue;
    specsHtml += '<div class="preset-spec-row">';
    specsHtml += '<span class="preset-spec-label">' + escapeAttr(label) + '</span>';
    specsHtml += '<span class="preset-spec-value">' + escapeAttr(value) + '</span>';
    specsHtml += '</div>';
  }
  specsHtml += '</div>';
  specsEl.innerHTML = specsHtml;

  descEl.textContent = item.description || '';

  if (extrasEl) {
    let extras = '';
    extras += renderAdvantages(Array.isArray(item.advantages) ? item.advantages : []);
    extras += renderFpsTable(Array.isArray(item.fpsList) ? item.fpsList : []);
    extrasEl.innerHTML = extras;
  }

  const cartBtn = document.getElementById('preset-detail-cart-btn');
  const configBtn = document.getElementById('preset-detail-config-btn');
  if (hasConfig) {
    if (cartBtn) {
      cartBtn.hidden = true;
      cartBtn.onclick = null;
    }
    if (configBtn) {
      configBtn.hidden = false;
      configBtn.textContent = 'Настроить';
      configBtn.onclick = (ev) => {
        ev?.stopPropagation();
        openTemplateConfigurator(item);
      };
    }
    if (bottomBar) bottomBar.classList.add('preset-detail-bottom-bar--config');
  } else {
    if (configBtn) configBtn.hidden = true;
    if (cartBtn) {
      cartBtn.hidden = false;
      cartBtn.onclick = (ev) => {
        ev?.stopPropagation();
        addToCartFromPreset(item);
      };
    }
    if (bottomBar) bottomBar.classList.remove('preset-detail-bottom-bar--config');
  }

  syncPresetCartButtonsUi();
  bindPresetDetailGoCartButton();
  showView('view-preset-detail');
}
